import * as React from 'react'
import Stack from '@mui/material/Stack'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'

import { ENTITY_TYPES } from '@sales-center/common'
import { OwnerSelect } from '@user/components/Owner'
import { IAddOpportunityFormModel } from '../../../typings'
import { FormSectionTitle } from '../../FormComponents/FormSectionTitle'
import { useManageOpportunityData } from './hooks/useManageOpportunityData'
import { SalesTeamSelect } from '../../FormComponents/SalesTeamSelect/SalesTeamSelect'
import { SalesFunnelSelect } from '../../FormComponents/SalesFunnelSelect/SalesFunnelSelect'
import { OpportunityStageMeter } from '../../FormComponents/OpportunityStageMeter/OpportunitystageMeter'
import { OpportunityStageSelect } from '../../FormComponents/OpportunityStageSelect/OpportunityStageSelect'

import { selectUserId } from '@user/store/authorization'
import { selectAllLeadOppProducts } from '@user/store/leadOppProducts'
import { useGetOpportunityIsCloseableModel } from '@user/store/opportunity'
import { ScrollToErrorMarker } from '@user/components/Formik/ScrollToErrorMarker'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import ClosedReason from '../../FormComponents/ClosedReason/ClosedReason'
import { selectAllEntityFormSettings } from '@user/store/entityFormSettings'
import { OppStageIndicatorTypes } from '@user/components/Settings/EntityFormSettings/FieldSettingsCard/typings'
import { useGetSalesTeamPermissionMessages } from '@user/store/salesTeam'

type Props = {
  branchId: string
  onClosedWonChange: (isClosedWon: boolean, isClosedLost: boolean) => void
}

export function AssignmentSection(props: Props) {
  const { branchId, onClosedWonChange } = props

  const entityFormSettings = useSelector(selectAllEntityFormSettings)

  const isOpportunityStageMeter =
    (entityFormSettings.find(
      (setting) => setting.fieldId === 'opportunityStageId',
    )?.options || OppStageIndicatorTypes.meter) === OppStageIndicatorTypes.meter

  const id = useSelector(selectUserId)

  const { values } = useFormikContext<IAddOpportunityFormModel>()

  const { salesTeamId, ownerId } = values

  const {
    isCloseableDetails,
    setOpportunity,
    status: isCloseableStatus,
  } = useGetOpportunityIsCloseableModel()

  const services = useSelector(selectAllLeadOppProducts)
  const disableFunnelStagesOptions = isCloseableStatus === 'pending'
  useManageOpportunityData({ values, setOpportunity, services })

  const { positionBasedAccessText, selectedSalesTeamAccessText } =
    useGetSalesTeamPermissionMessages('success', +salesTeamId, ownerId)

  return (
    <Stack gap={2} pb={4}>
      <FormSectionTitle>Assignment</FormSectionTitle>
      <Box>
        <SalesTeamSelect
          name="salesTeamId"
          branchId={branchId}
          errorText={selectedSalesTeamAccessText}
        />
      </Box>
      <Box>
        <OwnerSelect
          salesTeamId={values.salesTeamId}
          disabled={false}
          entityType={ENTITY_TYPES.Opportunity}
          name="ownerId"
          required
          defaultValue={ownerId || id}
          noTouchValidation
          errorText={positionBasedAccessText}
        />
      </Box>
      <SalesFunnelSelect name="salesFunnelId" />
      <ScrollToErrorMarker fieldName={'opportunityStageId'} />
      <Box sx={{ mt: -2 }}>
        {isOpportunityStageMeter ? (
          <OpportunityStageMeter
            name="opportunityStageId"
            disableOptions={disableFunnelStagesOptions}
            isCloseableDetails={isCloseableDetails}
            onClosedWonChange={onClosedWonChange}
          />
        ) : (
          <OpportunityStageSelect
            name="opportunityStageId"
            disableOptions={disableFunnelStagesOptions}
            isCloseableDetails={isCloseableDetails}
            onClosedWonChange={onClosedWonChange}
          />
        )}

        <Collapse
          in={values.opportunityStageName == 'Closed/Lost'}
          unmountOnExit
        >
          <ClosedReason isOpportunityStageMeter={isOpportunityStageMeter} />
        </Collapse>
      </Box>
    </Stack>
  )
}
